import { useRef, useState } from 'react';
import { Box } from '@mui/material';
import { useCelebration } from './celebrate';
import { random, useCanvasAnimation } from './useCanvasAnimation';

const SLEIGH_WIDTH = 170;
const FLY_SPEED = 0.3; // px per ms - about 5s across a laptop screen
const SILHOUETTE = '#e6edf4';
const DIM_FADE = 400; // ms for the night-sky band to fade in / out
const NOSE = '#ff4a4a';

const Reindeer = ({ x }: { x: number }) => (
  <g transform={`translate(${x} 0)`}>
    <ellipse cx='11' cy='20' rx='8' ry='4' />
    <path d='M4 18 l-2 -6' stroke={SILHOUETTE} strokeWidth='2.4' />
    <circle cx='2' cy='11' r='2.6' />
    <path d='M2 9 l-1 -5 M2 9 l3 -4 M1 6 l-3 -1' stroke={SILHOUETTE} strokeWidth='1' fill='none' />
    <path d='M6 23 l-3 6 M9 23 l-1 7 M14 23 l2 6 M17 22 l4 5' stroke={SILHOUETTE} strokeWidth='1.3' />
  </g>
);

/** Christmas: Santa's sleigh flies across the header, leaving a trail of sparkles, each time a policy binds. */
export const SleighFlyover = () => {
  const sleighRef = useRef<SVGSVGElement>(null);
  const dimRef = useRef<HTMLDivElement>(null);
  const [runKey, setRunKey] = useState(0);
  useCelebration('bind', () => setRunKey((key) => key + 1));

  const canvasRef = useCanvasAnimation({
    enabled: runKey > 0,
    runKey,
    createStep: ({ width }) => {
      const sparkles: { x: number; y: number; vx: number; vy: number; life: number }[] = [];
      let x = width + 10;
      let elapsed = 0;
      let fadeOut = 0;

      return (ctx, dt) => {
        elapsed += dt;
        x -= FLY_SPEED * dt;
        // a gentle bob, drifting slightly lower as it crosses
        const y = 14 + Math.sin(elapsed / 450) * 4 + (1 - x / width) * 10;
        const sleigh = sleighRef.current;
        if (sleigh) {
          sleigh.style.transform = `translate3d(${x}px, ${y}px, 0)`;
          sleigh.style.visibility = 'visible';
        }

        if (x > -SLEIGH_WIDTH) {
          for (let i = 0; i < 2; i++) {
            sparkles.push({
              x: x + SLEIGH_WIDTH - 10 + random(-3, 3),
              y: y + 30 + random(-4, 4),
              vx: random(0.01, 0.05),
              vy: random(-0.01, 0.025),
              life: 1,
            });
          }
        }

        let alive = 0;
        for (const s of sparkles) {
          if (s.life <= 0) continue;
          s.life -= dt / 900;
          s.x += s.vx * dt;
          s.y += s.vy * dt;
          if (s.life <= 0) continue;
          alive++;
          ctx.fillStyle = `rgba(255, 222, 140, ${s.life})`;
          ctx.beginPath();
          ctx.arc(s.x, s.y, 1.6 * s.life + 0.4, 0, Math.PI * 2);
          ctx.fill();
        }

        // dim the header behind the sleigh so it stands out: fade in as it arrives, out once it and the sparkles are gone
        const isGone = x <= -SLEIGH_WIDTH && alive === 0;
        if (isGone) fadeOut += dt;
        const dimOpacity = Math.min(elapsed / DIM_FADE, 1) * (1 - Math.min(fadeOut / DIM_FADE, 1));
        if (dimRef.current) dimRef.current.style.opacity = String(dimOpacity);

        const isDone = isGone && fadeOut >= DIM_FADE;
        if (isDone && sleigh) sleigh.style.visibility = 'hidden';
        return !isDone;
      };
    },
  });

  return (
    <Box sx={sxStyles.container} aria-hidden>
      <Box ref={dimRef} sx={sxStyles.dim} />
      <Box component='canvas' ref={canvasRef} sx={sxStyles.canvas} />
      <svg
        ref={sleighRef}
        viewBox='0 0 170 40'
        width={SLEIGH_WIDTH}
        height={40}
        fill={SILHOUETTE}
        style={{ position: 'absolute', top: 0, left: 0, visibility: 'hidden' }}
      >
        <Reindeer x={0} />
        <Reindeer x={24} />
        <Reindeer x={48} />
        <circle cx='0' cy='11' r='1.8' fill={NOSE} />
        <path d='M19 20 L 98 22 M43 20 L 98 22 M67 20 L98 22' stroke={SILHOUETTE} strokeWidth='.8' />
        <path d='M96 14 h38 q10 0 14 -10 l5 2 q-4 20 -20 20 h-37 z' />
        <path d='M92 32 h56 q7 0 9 -7' stroke={SILHOUETTE} strokeWidth='2' fill='none' />
        <path d='M104 26 v6 M138 26 v6' stroke={SILHOUETTE} strokeWidth='1.5' />
        <circle cx='126' cy='8' r='5' />
        <circle cx='126' cy='2.5' r='2' fill={NOSE} />
      </svg>
    </Box>
  );
};

const sxStyles = {
  container: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: 120,
    pointerEvents: 'none',
    overflow: 'hidden',
    zIndex: 1250,
  },
  dim: {
    position: 'absolute',
    inset: 0,
    opacity: 0,
    background: 'linear-gradient(to bottom, rgba(4, 10, 24, 0.6), rgba(4, 10, 24, 0.45) 70%, rgba(4, 10, 24, 0))',
  },
  canvas: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
  },
};
